import { useCallback } from 'react';
import styled from 'styled-components';

import {
  useAgentActions,
  useAgentState,
} from '#/features/terminal/agent-context';
import { useTerminalDrawerActions } from '#/features/terminal/drawer-context';

interface Example {
  readonly prompt: string;
  readonly hint: string;
}

interface Group {
  readonly title: string;
  readonly examples: readonly Example[];
}

// Block-trade-flavored prompts. The agent has the live trade-tape filters
// and current account context (see PARADIGM_BLOCK_TRADE_PREAMBLE in
// BlockTerminalTab.tsx) so phrases like "the tape", "today", and
// "my flow" already mean the user's tape view and counterparty.
const GROUPS: readonly Group[] = [
  {
    title: 'flow recap',
    examples: [
      {
        prompt: 'Summarize the last hour of BTC option block trades.',
        hint: 'Counts, total premium, dominant strikes/expiries.',
      },
      {
        prompt: "What's flowing through the tape right now?",
        hint: 'Live read on size, structure, and any unusual prints.',
      },
      {
        prompt: 'List the most active expiries for BTC and ETH today.',
        hint: 'Top 5 expiries by notional / contract count.',
      },
    ],
  },
  {
    title: 'structure analysis',
    examples: [
      {
        prompt:
          'Show me the largest ETH structures today and their implied vol.',
        hint: 'Multi-leg trades sorted by total premium with leg-by-leg IV.',
      },
      {
        prompt: 'Are there any unusual leg ratios in today’s flow?',
        hint: 'Non-standard ratios beyond the typical 1x1 spread / 1x2 ratio shapes.',
      },
      {
        prompt:
          'Compare the volume of strangles vs straddles in BTC this session.',
        hint: 'Helps gauge directional vs volatility positioning.',
      },
    ],
  },
  {
    title: 'positioning',
    examples: [
      {
        prompt: "What's the put-call ratio in BTC blocks this session?",
        hint: 'Hedging vs upside-bet color from the option flow.',
      },
      {
        prompt: 'Identify the dominant counterparty type in today’s ETH flow.',
        hint: 'Maker vs taker bias, average ticket size, repeat structures.',
      },
    ],
  },
];

export default function ExamplesView() {
  const agentActions = useAgentActions();
  const agentState = useAgentState();
  const drawerActions = useTerminalDrawerActions();

  const isReady = agentState.status === 'authenticated';

  const tryPrompt = useCallback(
    (prompt: string) => {
      if (!isReady) return;
      agentActions.sendMessage(prompt);
      drawerActions.setActiveTab('chat');
    },
    [isReady, agentActions, drawerActions],
  );

  return (
    <Scroll>
      <Intro>
        <h3>Block-trade prompts</h3>
        <p>
          Click <em>Run</em> to send a prompt to the agent. Each one assumes the
          live trade-tape filters and your current account context, so phrases
          like &quot;the tape&quot;, &quot;today&quot;, and &quot;my flow&quot;
          already resolve to your view.
        </p>
        {!isReady && (
          <NotReady>
            Connect and pair the agent first — the Run buttons activate once the
            connection badge turns green.
          </NotReady>
        )}
      </Intro>
      {GROUPS.map((group) => (
        <Group key={group.title}>
          <GroupTitle>{group.title}</GroupTitle>
          {group.examples.map((ex) => (
            <Card key={ex.prompt}>
              <Prompt>{ex.prompt}</Prompt>
              <Hint>{ex.hint}</Hint>
              <RunButton
                type="button"
                disabled={!isReady}
                onClick={() => tryPrompt(ex.prompt)}
                title={isReady ? 'Send to agent' : 'Not connected'}
              >
                Run
              </RunButton>
            </Card>
          ))}
        </Group>
      ))}
    </Scroll>
  );
}

const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Intro = styled.div`
  font-size: 0.8125rem;
  color: var(--text-secondary);

  h3 {
    margin: 0 0 6px 0;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--text-primary);
  }

  p {
    margin: 0;
    line-height: 1.5;
  }
`;

const NotReady = styled.div`
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 6px;
  background: color-mix(
    in srgb,
    var(--warning-color, #f5a623) 10%,
    transparent
  );
  border: 1px solid
    color-mix(in srgb, var(--warning-color, #f5a623) 30%, transparent);
  color: var(--text-primary);
  font-size: 0.75rem;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupTitle = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-secondary);
`;

const Card = styled.div`
  position: relative;
  padding: 10px 12px 12px;
  border: 1px solid var(--border-color);
  border-radius: 7px;
  background: color-mix(in srgb, var(--bg-color-container) 60%, transparent);
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Prompt = styled.div`
  font-size: 0.8125rem;
  color: var(--text-primary);
  line-height: 1.4;
  padding-right: 56px;
`;

const Hint = styled.div`
  font-size: 0.6875rem;
  color: var(--text-secondary);
  line-height: 1.4;
  padding-right: 56px;
`;

const RunButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 0.6875rem;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid var(--border-color);
  background: var(--bg-color-container);
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--hover-bg);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;
